// axiosInstance.js
import axios from 'axios';
import { getSafeApiPathFromAxiosConfig, recordInsightApiHttpError } from '../rum/initRum';
import { API_BASE_URL } from './apiConfig';
import Cookies from 'universal-cookie';
import { getCurrentUser, refreshSession } from '../utils/CognitoCustomChallengeService';
import { refreshAccessToken as authPortalRefreshAccessToken } from '../utils/authPortalAuth';
import { getAuthPortalLoginUrl } from '../utils/authPortal';
import dialogEventEmitter from '../utils/dialogEventEmitter';

const cookies = new Cookies();
const SUPPORT_ACCESS_LEVELS = ['s1', 's2', 's3'];
const DEFAULT_SYNC_LEAD_SECONDS = 60;

function isSupportSessionSubject() {
  if (typeof window === 'undefined') return false;
  const accessLevel = localStorage.getItem('accessLevel');
  return SUPPORT_ACCESS_LEVELS.includes(accessLevel) || !!cookies.get('userSubRole');
}

/** Prevent /insightappcom/insightappcom/... when baseURL and url both include the apiHost path prefix. */
function normalizeAxiosUrl(config) {
  const url = config.url;
  const baseURL = config.baseURL;
  if (!url || typeof url !== 'string' || !baseURL || /^https?:\/\//i.test(url)) {
    return;
  }
  const basePath = baseURL.replace(/^https?:\/\/[^/?#]+/, '').replace(/\/+$/, '');
  if (!basePath) {
    return;
  }
  const urlPath = url.startsWith('/') ? url : `/${url}`;
  if (urlPath === basePath || urlPath.startsWith(`${basePath}/`)) {
    const originMatch = baseURL.match(/^https?:\/\/[^/?#]+/i);
    config.baseURL = originMatch ? originMatch[0] : '';
    config.url = urlPath;
  }
}

let apiRequestTimeoutUserMessage = 'This is taking longer than expected. Please check your connection and try again.';

/**
 * Extract session TTL info from various API response formats.
 * Returns null if no session info found.
 */
function extractSessionTtlInfo(data, url) {
  // postUserLogin response: data.supportUserInfo or top-level data fields
  if (data?.data?.supportUserInfo?.sessionSecondsRemaining !== undefined) {
    const info = data.data.supportUserInfo;
    return {
      sessionSecondsRemaining: Number(info.sessionSecondsRemaining) || 0,
      sessionWarningActive: info.sessionWarningActive === '1' || info.sessionWarningActive === 1,
      sessionTtlSyncLeadSeconds: Number(info.sessionTtlSyncLeadSeconds) || DEFAULT_SYNC_LEAD_SECONDS
    };
  }

  if (data?.data?.sessionSecondsRemaining !== undefined) {
    const info = data.data;
    return {
      sessionSecondsRemaining: Number(info.sessionSecondsRemaining) || 0,
      sessionWarningActive: info.sessionWarningActive === '1' || info.sessionWarningActive === 1,
      sessionTtlSyncLeadSeconds: Number(info.sessionTtlSyncLeadSeconds) || DEFAULT_SYNC_LEAD_SECONDS
    };
  }

  // configureSupportSession (START_SUPPORT_SESSION) response: info block
  if (data?.info?.sessionSecondsRemaining !== undefined) {
    const info = data.info;
    return {
      sessionSecondsRemaining: Number(info.sessionSecondsRemaining) || 0,
      sessionWarningActive: info.sessionWarningActive === '1' || info.sessionWarningActive === 1,
      sessionTtlSyncLeadSeconds: Number(info.sessionTtlSyncLeadSeconds) || DEFAULT_SYNC_LEAD_SECONDS
    };
  }

  // sessionactivity / continuesession response: details block
  if (data?.details?.sessionSecondsRemaining !== undefined) {
    const details = data.details;
    return {
      sessionSecondsRemaining: Number(details.sessionSecondsRemaining) || 0,
      sessionWarningActive: details.sessionWarningActive === '1' || details.sessionWarningActive === 1,
      sessionTtlSyncLeadSeconds: Number(details.sessionTtlSyncLeadSeconds) || DEFAULT_SYNC_LEAD_SECONDS
    };
  }

  return null;
}

/**
 * Handle support session TTL data from API responses.
 * Emits events for session initialization, sync updates, warnings, or expiry.
 */
function handleSupportSessionResponse(data, url) {
  if (!isSupportSessionSubject()) return;

  const responseCode = data?.response?.responseCode;

  if (responseCode === '44030' || responseCode === 44030) {
    dialogEventEmitter.emit('supportSessionExpired');
    return;
  }

  // Handle session warning (44033 - TTL <= warning threshold)
  if (responseCode === '44033' || responseCode === 44033) {
    const ttlInfo = extractSessionTtlInfo(data, url);
    dialogEventEmitter.emit('supportSessionWarning', {
      sessionSecondsRemaining: ttlInfo?.sessionSecondsRemaining || 0,
      sessionTtlSyncLeadSeconds: ttlInfo?.sessionTtlSyncLeadSeconds || DEFAULT_SYNC_LEAD_SECONDS
    });
    return;
  }

  // Extract TTL info from successful responses
  const ttlInfo = extractSessionTtlInfo(data, url);
  if (ttlInfo) {
    const ttl = Number(ttlInfo.sessionSecondsRemaining);
    const warningActive = ttlInfo.sessionWarningActive === true
      || ttlInfo.sessionWarningActive === '1'
      || ttlInfo.sessionWarningActive === 1;
    if (warningActive || (Number.isFinite(ttl) && ttl > 0)) {
      dialogEventEmitter.emit('supportSessionTtlUpdate', ttlInfo);
    }
  }
}

/** Called from `Locales.jsx` when messages load — key `apiRequestTimeout` in locale JSON. */
export function registerApiRequestTimeoutMessage(messages) {
  const text = messages?.apiRequestTimeout;
  if (typeof text === 'string' && text.trim()) {
    apiRequestTimeoutUserMessage = text;
  }
}

function getApiRequestTimeoutUserMessage() {
  return apiRequestTimeoutUserMessage;
}

function isAxiosRequestTimeout(error) {
  if (!error || error.response != null) return false;
  return error.code === 'ECONNABORTED';
}

/** When true, use auth portal (auth-qa) POST /v1/auth/refresh with cookies instead of Cognito refresh. */
const useAuthPortalRefresh = () => !!process.env.authUrl;

// Create Axios instance with default configuration
// Auth: With OC login, tokens are in HTTP-only cookies — no token header; browser sends cookies (withCredentials: true).
// Insight API must accept auth from Cookie when token header is absent. With Cognito, token is sent in header from localStorage.
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // Increased timeout to 60 seconds
  // Additional default config can be added here:
  withCredentials: true, // Include cookies in cross-origin requests
  // validateStatus: (status) => status < 400, // Custom status validation
});

// Add request interceptor to modify the request before sending
// Usage examples for dynamic header exclusion:
// config.excludeHeaders = ['apiKey'] - exclude single header
// config.excludeHeaders = ['apiKey', 'token'] - exclude multiple headers  
// config.excludeHeaders = 'apiKey' - exclude single header as string
// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      // Mark errors from refresh failure so they can be distinguished from retry errors
      if (!error.isRefreshFailure) {
        error.isRefreshFailure = true;
      }
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Gets the access token from localStorage
 * @returns {string|null} The access token or null if not found
 */
const getAccessToken = () => {
  return localStorage.getItem('token') || localStorage.getItem('access_token');
};

/**
 * Gets the refresh token from localStorage
 * @returns {string|null} The refresh token or null if not found
 */
const getRefreshToken = () => {
  return localStorage.getItem('refreshtoken') || localStorage.getItem('refresh_token');
};

/**
 * Clears all authentication tokens from localStorage
 */
const clearTokens = () => {
  localStorage.removeItem('idtoken');
  localStorage.removeItem('token');
  localStorage.removeItem('refreshtoken');
  localStorage.removeItem('id_token');
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('serviceToken');
};

/**
 * Redirects to login page if not already there
 */
const redirectToLogin = () => {
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

/**
 * Updates the request with a token and retries it (Cognito / localStorage flow).
 * @param {Object} request - The axios request config
 * @param {string|null} token - The token to use (if null, will fetch from localStorage)
 * @returns {Promise} Promise that resolves with the retried request
 */
const retryRequestWithToken = (request, token = null) => {
  const finalToken = token || getAccessToken();
  request.headers.token = finalToken;
  return axiosInstance(request);
};

/**
 * Retries the request with credentials only (auth portal cookie flow). No token header.
 * @param {Object} request - The axios request config
 * @returns {Promise} Promise that resolves with the retried request
 */
const retryRequestWithCredentials = (request) => {
  return axiosInstance(request);
};

/**
 * Handles refresh failure by clearing tokens, redirecting to login, and rejecting with error.
 * When using auth portal (authUrl), redirects to auth portal login instead of in-app /login.
 * Effectively "logout on refresh token expiration" for both Cognito and auth portal flows.
 * @param {Error} error - The error to reject with
 * @returns {Promise} Rejected promise with the error
 */
const handleRefreshFailure = (error) => {
  clearTokens();
  if (useAuthPortalRefresh()) {
    window.location.href = getAuthPortalLoginUrl();
  } else {
    redirectToLogin();
  }
  return Promise.reject(error);
};

/**
 * Handles token refresh flow for expired sessions.
 * Uses auth portal refresh (POST /v1/auth/refresh with cookies) when process.env.authUrl is set,
 * otherwise Cognito refresh (same queue/retry logic for both).
 *
 * @param {Object} originalRequest - The original axios request config
 * @param {Error} originalError - Optional original error to reject if refresh fails (for error interceptor)
 * @returns {Promise} Promise that resolves with retried request or rejects with error
 */
const handleTokenRefresh = async (originalRequest, originalError = null) => {
  const useAuthPortal = useAuthPortalRefresh();

  // --- Auth portal (cookie) flow: refresh via auth-qa, retry with credentials ---
  if (useAuthPortal) {
    if (!isRefreshing) {
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        await authPortalRefreshAccessToken();
        processQueue(null, null);
        isRefreshing = false;
        return retryRequestWithCredentials(originalRequest);
      } catch (error) {
        console.error('Auth portal refresh failed:', error);
        processQueue(error, null);
        isRefreshing = false;
        return handleRefreshFailure(error);
      }
    }
    originalRequest._retry = true;
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then(() => retryRequestWithCredentials(originalRequest))
      .catch(err => {
        if (err.isRefreshFailure) return handleRefreshFailure(err);
        return Promise.reject(err);
      });
  }

  // --- Cognito (localStorage) flow ---
  const refreshTokenValue = getRefreshToken();

  // Case 1: Refresh token available and not currently refreshing
  if (refreshTokenValue && !isRefreshing) {
    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const cognitoUser = getCurrentUser();
      if (!cognitoUser) {
        throw new Error('No Cognito user available');
      }

      await refreshSession(cognitoUser, refreshTokenValue);

      // Get the new token after refresh
      const newToken = getAccessToken();

      // Process queued requests with the new token BEFORE resetting the flag
      processQueue(null, newToken);
      isRefreshing = false;

      return retryRequestWithToken(originalRequest, newToken);
    } catch (error) {
      console.error('Error refreshing token:', error);
      processQueue(error, null);
      isRefreshing = false;
      return handleRefreshFailure(error);
    }
  }
  // Case 2: Already refreshing, queue this request
  if (isRefreshing) {
    originalRequest._retry = true;
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then(token => retryRequestWithToken(originalRequest, token))
      .catch(err => {
        if (err.isRefreshFailure) return handleRefreshFailure(err);
        return Promise.reject(err);
      });
  }
  // Case 3: No refresh token available
  const error = originalError || new Error('Invalid session and no refresh token available');
  return handleRefreshFailure(error);
};

axiosInstance.interceptors.request.use(
  (config) => {
    normalizeAxiosUrl(config);

    // Ensure headers object exists
    config.headers = config.headers || {};

    // Handle dynamic header exclusion
    const excludeHeaders = config.excludeHeaders || [];
    const excludedList = Array.isArray(excludeHeaders) ? excludeHeaders : [excludeHeaders];

    const useAuthPortal = useAuthPortalRefresh();

    // Default headers configuration
    const defaultHeaders = {
      accountId: cookies.get('accountId'),
      apiKey: process.env.apiKey
    };

    // Auth portal (OC login): tokens are in HTTP-only cookies — do NOT set token header.
    // Browser sends cookies automatically (withCredentials: true). Insight API reads auth from Cookie.
    // localAppCom: same cookie flow via Vite proxy to Jetty (CookieToHeaderFilter on backend).
    // Cognito (localStorage): send access token in header for every request.
    if (!useAuthPortal) {
      const accessToken = getAccessToken();
      if (accessToken && !excludedList.includes('token')) {
        config.headers.token = accessToken;
      }
    }

    // Add default headers unless excluded
    Object.keys(defaultHeaders).forEach(headerKey => {
      if (!excludedList.includes(headerKey) && defaultHeaders[headerKey]) {
        config.headers[headerKey] = defaultHeaders[headerKey];
      }
    });

    // Optional headers configuration
    // userId header: Insight APIs expect internal user id (`_Id`), not the `userid` cookie when it holds Cognito username (e.g. QA-203-…).
    const optionalHeaderMap = {
      _Id: cookies.get("_Id"),
      userId: cookies.get("_Id") || cookies.get("userid") || cookies.get("userId"),
      networkId: (() => {
        // Try multiple sources for networkId
        const currentNetwork = localStorage.getItem("currentNetwork");
        const networkId = localStorage.getItem("networkId");
        const cookieNetwork = cookies.get("currentNetwork");
        // Also check selectedType if available
        let selectedTypeNetwork = null;
        try {
          const selectedType = JSON.parse(localStorage.getItem("selectedType") || "{}");
          if (selectedType?.id && selectedType?.type === "Network") {
            selectedTypeNetwork = selectedType.id;
          }
        } catch (e) {
          // Ignore parse errors
        }
        return currentNetwork || networkId || cookieNetwork || selectedTypeNetwork || null;
      })(),
      lastUpdatedTime: "0",
      // Add more optional headers here as needed
    };

    // Add optional headers if specified in possibleHeaders
    (config.possibleHeaders || []).forEach((key) => {
      if (optionalHeaderMap[key] && !excludedList.includes(key)) {
        config.headers[key] = optionalHeaderMap[key];
      }
    });

    config.metadata = { ...(config.metadata || {}), startTime: Date.now() };
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle responses globally
axiosInstance.interceptors.response.use(
  async (response) => {
    const originalRequest = response.config;

    // Handle support session TTL data from API responses
    handleSupportSessionResponse(response.data, originalRequest.url);

    // Short-circuit for 44030 (session expired) - emit event and reject to prevent callers from processing expired payload
    const responseCode = response.data?.response?.responseCode;
    if (isSupportSessionSubject() && (responseCode === '44030' || responseCode === 44030)) {
      return Promise.reject({ isSupportSessionExpired: true, response });
    }

    if (response.data?.response?.message === "Invalid session" && !originalRequest._retry) {
      return handleTokenRefresh(originalRequest);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const start = originalRequest?.metadata?.startTime;
    const status = error.response?.status ?? 0;
    if (start && status >= 400 && status < 600) {
      recordInsightApiHttpError({
        durationMs: Date.now() - start,
        method: originalRequest.method,
        httpStatus: status,
        path: getSafeApiPathFromAxiosConfig(originalRequest)
      });
    }

    // Handle 401 Unauthorized - try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      return handleTokenRefresh(originalRequest, error);
    }

    // Handle 400 Bad Request in auth portal mode.
    // The Insight API may return 400 (not 401) when the accessToken cookie is absent or expired.
    // Trigger refresh only when the body text clearly indicates an auth/session problem — not on
    // generic 400s ("bad request", empty body) or normal validation errors, which would otherwise
    // cause unnecessary refresh cycles and possible session disruption.
    if (
      error.response?.status === 400 &&
      !originalRequest._retry &&
      useAuthPortalRefresh()
    ) {
      const body = error.response?.data;
      const pickStr = (v) => (typeof v === 'string' ? v : '');
      const rawMsg = (
        pickStr(body?.message) ||
        pickStr(body?.response?.message) ||
        pickStr(typeof body?.error === 'string' ? body.error : '') ||
        ''
      ).toLowerCase().trim();

      // Phrases typical of auth/gateway JWT/session failures — avoid single words like "token" or
      // "expired" alone (they appear in business validation messages).
      const AUTH_400_PHRASES = [
        'invalid session',
        'unauthorized',
        'authentication',
        'jwt',
        'bearer',
        'access token',
        'refresh token',
        'invalid token',
        'token expired',
        'expired token',
        'invalid or expired',
        'not supplied',
      ];
      const isAuthRelated = AUTH_400_PHRASES.some((phrase) => rawMsg.includes(phrase));

      if (isAuthRelated) {
        return handleTokenRefresh(originalRequest, error);
      }
    }

    if (error.response?.status === 403) {
      // Handle forbidden access
    } else if (error.response?.status >= 500) {
      // Handle server errors
    }

    if (isAxiosRequestTimeout(error)) {
      if (error.originalMessage === undefined) {
        error.originalMessage = error.message;
      }
      error.isRequestTimeout = true;
      error.message = getApiRequestTimeoutUserMessage();
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
